import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../../config.js';
import { useFetchWrapper } from '../../_helpers/fetchWrapper.js';
import { isSignedIn, rememberReturnPath } from '../../_helpers/safeRedirect.js';
import { useT } from '../../contexts/LocaleContext.jsx';
import '../../styles/FollowButton.css';

/**
 * Follow / Following toggle for a band (ENG-519).
 *
 * `viewerRole` is the band response's `viewer_role`: `follower` means already
 * following and `none` means not. Members and moderators get no control: they
 * run the band, and a moderator's role hides whether they also follow it. A
 * signed-out visitor gets a Follow button that goes to sign-in and back.
 *
 * The toggle is optimistic: it flips at once and reports the change through
 * `onFollowChange(+1 | -1)` so a follower count can move with it, then rolls
 * both back with an error if the request fails. One request at a time; a
 * repeat click while one is in flight is ignored.
 */
const FollowButton = ({ bandId, viewerRole, onFollowChange, className = '' }) => {
  const t = useT();
  const navigate = useNavigate();
  const fetchWrapper = useFetchWrapper();
  const [following, setFollowing] = useState(viewerRole === 'follower');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const busyRef = useRef(false);

  // A new band or role resets the toggle, adjusted during render rather than
  // in an effect: a mount effect still pending when the first click lands
  // would run after it and undo the optimistic flip.
  const [shownFor, setShownFor] = useState({ viewerRole, bandId });
  if (shownFor.viewerRole !== viewerRole || shownFor.bandId !== bandId) {
    setShownFor({ viewerRole, bandId });
    setFollowing(viewerRole === 'follower');
    setError(null);
  }

  const classes = `follow-button ${className}`.trim();

  if (!isSignedIn()) {
    return (
      <button
        type="button"
        className={classes}
        data-testid="follow-sign-in"
        title={t('followButton.signIn')}
        onClick={() => {
          rememberReturnPath();
          navigate('/login');
        }}
      >
        {t('followButton.follow')}
      </button>
    );
  }

  if (viewerRole !== 'follower' && viewerRole !== 'none') return null;

  const toggle = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    const next = !following;
    setFollowing(next);
    onFollowChange?.(next ? 1 : -1);
    try {
      if (next) {
        await fetchWrapper.post(`${config.apiBaseUrl}/bands/${bandId}/follow`, {});
      } else {
        await fetchWrapper.delete(`${config.apiBaseUrl}/bands/${bandId}/unfollow`);
      }
    } catch (err) {
      // A follow the server already has (from another tab) is where we wanted
      // to be; anything else puts the button and the count back.
      if (!(next && /already following/i.test(err?.message ?? ''))) {
        console.error('Error changing follow:', err);
        setFollowing(!next);
        onFollowChange?.(next ? -1 : 1);
        setError(t(next ? 'followButton.errors.follow' : 'followButton.errors.unfollow'));
      }
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  return (
    <span className="follow-button-wrap">
      <button
        type="button"
        className={`${classes}${following ? ' follow-button--following' : ''}`}
        aria-pressed={following}
        disabled={busy}
        onClick={toggle}
        data-testid="follow-button"
      >
        {following ? t('followButton.following') : t('followButton.follow')}
      </button>
      {error && <span className="follow-button-error" role="alert">{error}</span>}
    </span>
  );
};

export default FollowButton;
